import { Controller } from "@hotwired/stimulus"

const NEEDS_AMOUNT = ["Refund", "Credits"]
const NEEDS_DISPOSITION = ["Replacement", "Credits", "Refund"]

// Maps internal UI action type values to the server-side action_type param.
// "GeneralCredits" is a UI-only state — it submits as "Credits".
const SERVER_ACTION_TYPE = { "GeneralCredits": "Credits" }

// Human-readable labels for submit button and summary headings.
const ACTION_TYPE_LABELS = {
  "Credits":        "Order Credits",
  "GeneralCredits": "General Credits"
}

export default class extends Controller {
  static targets = [
    "actionTypeBtn",
    "actionTypeInput",
    "itemRow",
    "itemCheckbox",
    "selectAllCheckbox",
    "configSection",
    "itemCard",
    "chargebackInfo",
    "generalCreditsForm",
    "helperText",
    "shippingSection",
    "shippingInput",
    "shippingCapHint",
    "summaryBox",
    "summaryTitle",
    "summaryContent",
    "submitBtn",
    "submitLabel",
    "selectedCountBadge"
  ]

  static values = {
    actionType:              { type: String, default: "" },
    creditsRemaining:        { type: Number, default: 0 },
    shippingCharge:          { type: Number, default: 0 },
    shippingRefundRemaining: { type: Number, default: 0 },
    shippingCreditsRemaining: { type: Number, default: 0 }
  }

  connect() {
    this.shippingDirty = false
    this._renderAll()
  }

  // ── Action type ──────────────────────────────────────────────────────────────

  selectActionType(event) {
    this.actionTypeValue = event.params.type
    this.actionTypeInputTarget.value = SERVER_ACTION_TYPE[event.params.type] ?? event.params.type

    // Reset all per-item configs when switching action type
    this.itemCardTargets.forEach(card => {
      card.querySelectorAll("select").forEach(s => s.value = "")
      card.querySelectorAll("input[type=number]").forEach(i => {
        const itemTotal = parseFloat(i.dataset.itemTotal)
        i.value = isNaN(itemTotal) ? "" : itemTotal.toFixed(2)
        i.closest(".amount-field-wrapper")?.querySelector(".amount-error")?.remove()
      })
    })

    if (this.hasGeneralCreditsFormTarget) {
      this.generalCreditsFormTarget.querySelectorAll("select").forEach(s => s.value = "")
      this.generalCreditsFormTarget.querySelectorAll("input[type=number]").forEach(i => i.value = "")
    }

    if (this.hasShippingInputTarget) {
      this.shippingInputTarget.value = "0.00"
      this.shippingInputTarget.closest(".amount-field-wrapper")?.querySelector(".amount-error")?.remove()
    }
    this.shippingDirty = false

    this._renderAll()
  }

  actionTypeValueChanged() {
    this.actionTypeBtnTargets.forEach(btn => {
      const isSelected = btn.dataset.shipmentActionTypeParam === this.actionTypeValue
      btn.classList.toggle("bg-gray-900", isSelected)
      btn.classList.toggle("text-white", isSelected)
      btn.classList.toggle("border-gray-900", isSelected)
      btn.classList.toggle("border", !isSelected)
      btn.classList.toggle("border-gray-300", !isSelected)
      btn.classList.toggle("text-gray-700", !isSelected)
      btn.classList.toggle("bg-white", !isSelected)
    })

    if (this.hasSubmitLabelTarget) {
      const label = ACTION_TYPE_LABELS[this.actionTypeValue] ?? this.actionTypeValue
      this.submitLabelTarget.textContent = label ? `Submit ${label}` : "Submit Action"
    }
  }

  // ── Item selection ────────────────────────────────────────────────────────────

  toggleSelectAll(event) {
    const checked = event.target.checked
    this.itemCheckboxTargets.forEach(cb => { cb.checked = checked })
    this._renderAll()
  }

  toggleItem() {
    this._syncSelectAllState()
    this._renderAll()
  }

  // ── Amount / disposition changes ──────────────────────────────────────────────

  validateAmount(event) {
    const input = event.target
    if (this.hasShippingInputTarget && input === this.shippingInputTarget) {
      this.shippingDirty = true
    }
    const value = parseFloat(input.value)
    const cap = parseFloat(input.max)
    const wrapper = input.closest(".amount-field-wrapper")

    let errorEl = wrapper?.querySelector(".amount-error")
    if (!errorEl && wrapper) {
      errorEl = document.createElement("p")
      errorEl.className = "amount-error text-xs text-red-500 mt-1"
      wrapper.appendChild(errorEl)
    }

    if (isNaN(value) || value < 0) {
      if (errorEl) errorEl.textContent = "Amount must be $0.00 or more"
    } else if (!isNaN(cap) && value > cap) {
      if (errorEl) errorEl.textContent = `Cannot exceed eligible balance (${this._formatCurrency(cap)})`
    } else {
      if (errorEl) errorEl.textContent = ""
    }

    this._renderSubmitState()
    this._renderSummary()
  }

  dispositionChanged() {
    this._renderSubmitState()
    this._renderSummary()
  }

  // ── Private rendering helpers ─────────────────────────────────────────────────

  _renderAll() {
    this._renderItemCards()
    this._renderHelperText()
    this._renderGeneralCredits()
    this._renderChargebackInfo()
    this._renderShippingSection()
    this._renderSummary()
    this._renderSubmitState()
    this._renderSelectedCount()
  }

  get _selectedCheckboxes() {
    return this.itemCheckboxTargets.filter(cb => cb.checked)
  }

  get _selectedItemIds() {
    return this._selectedCheckboxes.map(cb => cb.value)
  }

  _syncSelectAllState() {
    if (!this.hasSelectAllCheckboxTarget) return
    const total = this.itemCheckboxTargets.length
    const selected = this._selectedCheckboxes.length
    this.selectAllCheckboxTarget.checked = total > 0 && selected === total
    this.selectAllCheckboxTarget.indeterminate = selected > 0 && selected < total
  }

  _renderSelectedCount() {
    if (!this.hasSelectedCountBadgeTarget) return
    this.selectedCountBadgeTarget.textContent = this._selectedCheckboxes.length
  }

  _renderItemCards() {
    const actionType = this.actionTypeValue
    const showCards = actionType !== "Chargeback"

    // Disable/uncheck checkboxes first so selectedIds reflects post-uncheck state.
    // Computing selectedIds before this loop would leave stale selections visible
    // for one render pass when switching to GeneralCredits or Chargeback.
    const blockSelection = actionType === "Chargeback" || actionType === "GeneralCredits"
    this.itemRowTargets.forEach(row => {
      const refundRemaining  = parseFloat(row.dataset.refundRemaining  || 9999)
      const creditsRemaining = parseFloat(row.dataset.creditsRemaining || 9999)
      const wasReplaced      = row.dataset.wasReplaced      === "true"
      const hasPriorRecovery = row.dataset.hasPriorRecovery === "true"

      const fullyRefunded      = actionType === "Refund"      && refundRemaining  <= 0
      const fullyCredited      = actionType === "Credits"     && creditsRemaining <= 0
      // Replaced items are ineligible for any further action
      const replacedBlock      = wasReplaced
      // Items with prior Credits/Refund cannot be replaced
      const priorRecoveryBlock = actionType === "Replacement" && hasPriorRecovery

      const cb = row.querySelector("input[type=checkbox]")
      if (cb) {
        const disabled = fullyRefunded || fullyCredited || blockSelection || replacedBlock || priorRecoveryBlock
        cb.disabled = disabled
        row.classList.toggle("opacity-40", disabled)
        row.classList.toggle("cursor-not-allowed", disabled)
        row.classList.toggle("cursor-pointer", !disabled)
        if (disabled) cb.checked = false
      }
    })
    if (this.hasSelectAllCheckboxTarget) {
      this.selectAllCheckboxTarget.disabled = blockSelection
      if (blockSelection) this.selectAllCheckboxTarget.checked = false
    }

    const selectedIds = new Set(this._selectedItemIds)

    this.itemCardTargets.forEach(card => {
      const itemId = card.dataset.itemId
      const visible = selectedIds.has(itemId) && showCards
      card.classList.toggle("hidden", !visible)
      this._syncItemCardFormControls(card, visible)

      if (visible) {
        const needsAmount = NEEDS_AMOUNT.includes(actionType)
        const amountSection = card.querySelector(".amount-section")
        const dispositionSection = card.querySelector(".disposition-section")

        if (amountSection) amountSection.classList.toggle("hidden", !needsAmount)
        if (dispositionSection) {
          dispositionSection.classList.toggle("sm:col-span-2", !needsAmount)
        }

        // Show the correct disposition select for this action type
        card.querySelectorAll("select[data-action-type]").forEach(sel => {
          const matches = sel.dataset.actionType === actionType
          sel.classList.toggle("hidden", !matches)
          sel.disabled = !matches
        })

        // Update amount label, cap hint, and max for Refund/Credits
        const amountInput = card.querySelector("input[type=number]")
        const amountLabel = card.querySelector(".amount-label")
        const capHint     = card.querySelector(".amount-cap-hint")
        if (amountInput && actionType === "Refund") {
          const remaining = parseFloat(card.dataset.refundRemaining ?? card.dataset.itemTotal)
          amountInput.max = remaining
          amountInput.value = remaining.toFixed(2)
          if (amountLabel) amountLabel.textContent = "Refund Amount"
          if (capHint) capHint.textContent = `(${this._formatCurrency(remaining)} eligible)`
        } else if (amountInput && actionType === "Credits") {
          const remaining = parseFloat(card.dataset.creditsRemaining ?? card.dataset.itemTotal)
          amountInput.max = remaining
          amountInput.value = remaining.toFixed(2)
          if (amountLabel) amountLabel.textContent = "Credit Amount"
          if (capHint) capHint.textContent = `(${this._formatCurrency(remaining)} eligible)`
        }
      }
    })

    // Show/hide config section wrapper
    if (this.hasConfigSectionTarget) {
      const hasCards = selectedIds.size > 0 && actionType !== "Chargeback"
      this.configSectionTarget.classList.toggle("hidden", !hasCards)
    }
  }

  _renderHelperText() {
    if (!this.hasHelperTextTarget) return
    const selectedCount = this._selectedCheckboxes.length
    const actionType = this.actionTypeValue

    if (!actionType) {
      this.helperTextTarget.classList.remove("hidden")
      this.helperTextTarget.textContent = "Select an action type above to begin"
      return
    }

    if (actionType === "Chargeback" || actionType === "GeneralCredits") {
      this.helperTextTarget.classList.add("hidden")
      return
    }

    const isGeneralCredits = actionType === "Credits" && selectedCount === 0
    const show = selectedCount === 0 && !isGeneralCredits
    this.helperTextTarget.classList.toggle("hidden", !show)
    if (show) {
      this.helperTextTarget.textContent =
        `Select one or more items to initiate a ${actionType.toLowerCase()} request`
    }
  }

  _renderGeneralCredits() {
    if (!this.hasGeneralCreditsFormTarget) return
    const show = this.actionTypeValue === "GeneralCredits"
    this.generalCreditsFormTarget.classList.toggle("hidden", !show)
    this._syncNamedControls(this.generalCreditsFormTarget, show)

    if (show) {
      const amtInput = this.generalCreditsFormTarget.querySelector("input[type=number]")
      if (amtInput && !amtInput.value && this.creditsRemainingValue > 0) {
        amtInput.value = this.creditsRemainingValue.toFixed(2)
      }
    }
  }

  _syncItemCardFormControls(card, selected) {
    const actionType = this.actionTypeValue

    card.querySelectorAll("select[data-action-type]").forEach(select => {
      const enabled = selected && select.dataset.actionType === actionType
      this._syncNamedControl(select, enabled)
      select.classList.toggle("hidden", !enabled)
    })

    const amountInput = card.querySelector("input[type=number][data-param-name]")
    if (amountInput) {
      this._syncNamedControl(amountInput, selected && NEEDS_AMOUNT.includes(actionType))
    }
  }

  _syncNamedControls(container, enabled) {
    container.querySelectorAll("[data-param-name]").forEach(control => {
      this._syncNamedControl(control, enabled)
    })
  }

  _syncNamedControl(control, enabled) {
    if (enabled) {
      control.name = control.dataset.paramName
      control.disabled = false
    } else {
      control.removeAttribute("name")
      control.disabled = true
    }
  }

  // Eligible = selectable under the current action type (not disabled by
  // _renderItemCards for being already exhausted, replaced, etc).
  get _eligibleItemCheckboxes() {
    return this.itemCheckboxTargets.filter(cb => !cb.disabled)
  }

  _allEligibleItemsSelected() {
    const eligible = this._eligibleItemCheckboxes
    return eligible.length > 0 && eligible.every(cb => cb.checked)
  }

  _renderShippingSection() {
    if (!this.hasShippingSectionTarget) return
    const actionType  = this.actionTypeValue
    const selectedIds = new Set(this._selectedItemIds)
    const hasItems    = selectedIds.size > 0
    const show = (actionType === "Refund" || actionType === "Credits") &&
                 hasItems &&
                 this.shippingChargeValue > 0
    this.shippingSectionTarget.classList.toggle("hidden", !show)
    this._syncNamedControls(this.shippingSectionTarget, show)

    if (!show) {
      // Leaving the shipping section entirely (no items selected, or an
      // action type that doesn't use it) resets the "session" — the next
      // time it appears, it should default fresh rather than remember a
      // stale manual edit from an earlier, unrelated selection.
      this.shippingDirty = false
      return
    }

    if (this.hasShippingInputTarget) {
      const remaining = actionType === "Refund"
        ? this.shippingRefundRemainingValue
        : this.shippingCreditsRemainingValue
      this.shippingInputTarget.max = remaining
      if (this.hasShippingCapHintTarget) {
        this.shippingCapHintTarget.textContent = `(${this._formatCurrency(remaining)} eligible)`
      }

      // Default to the full eligible amount only when every selectable item
      // is currently selected; otherwise default to $0. A manual edit by the
      // user (tracked via shippingDirty) always wins over this default.
      if (!this.shippingDirty) {
        const defaultValue = this._allEligibleItemsSelected() ? remaining : 0
        this.shippingInputTarget.value = defaultValue.toFixed(2)
      }
    }
  }

  _renderChargebackInfo() {
    if (!this.hasChargebackInfoTarget) return
    this.chargebackInfoTarget.classList.toggle("hidden", this.actionTypeValue !== "Chargeback")
  }

  _renderSummary() {
    if (!this.hasSummaryBoxTarget) return
    const actionType = this.actionTypeValue
    const selectedIds = new Set(this._selectedItemIds)
    const shouldShow = (selectedIds.size > 0 || actionType === "GeneralCredits") && actionType !== "Chargeback"
    this.summaryBoxTarget.classList.toggle("hidden", !shouldShow)
    if (!shouldShow || !this.hasSummaryContentTarget) return

    const content = this.summaryContentTarget

    if (actionType === "GeneralCredits") {
      const gcAmtInput = this.generalCreditsFormTarget?.querySelector("input[type=number]")
      const gcAmt = parseFloat(gcAmtInput?.value || 0)
      if (!isNaN(gcAmt) && gcAmt > 0) {
        content.innerHTML =
          `<div class="flex justify-between text-xs text-gray-700">
            <span class="italic">General Account Credits</span>
            <span class="font-medium">$${gcAmt.toFixed(2)}</span>
          </div>
          <div class="flex justify-between text-sm font-semibold border-t border-purple-300 pt-2 mt-1 text-purple-800">
            <span>Total General Credits</span>
            <span>$${gcAmt.toFixed(2)}</span>
          </div>`
      }
      return
    }

    if (actionType === "Replacement") {
      const rows = this.itemCardTargets
        .filter(card => selectedIds.has(card.dataset.itemId))
        .map(card => {
          const name = card.dataset.productName
          const disposition = card.querySelector("select:not([disabled])")?.value || "No disposition"
          return `<div class="flex items-start justify-between gap-2">
            <span class="text-xs text-gray-700 flex-1 leading-snug">${this._escHtml(name)}</span>
            <span class="text-xs px-2 py-0.5 rounded-full whitespace-nowrap bg-green-100 text-green-800">${this._escHtml(disposition)}</span>
          </div>`
        })
      content.innerHTML = rows.join("")
      return
    }

    if (actionType === "Refund" || actionType === "Credits") {
      let totalAmount = 0
      const rows = this.itemCardTargets
        .filter(card => selectedIds.has(card.dataset.itemId))
        .map(card => {
          const name = card.dataset.productName
          const amtInput = card.querySelector("input[type=number]")
          const itemTotal = parseFloat(card.dataset.itemTotal || 0)
          const amt = amtInput ? parseFloat(amtInput.value) : itemTotal
          const safeAmt = isNaN(amt) ? 0 : amt
          totalAmount += safeAmt
          return `<div class="flex justify-between text-xs text-gray-700">
            <span class="truncate flex-1 pr-2">${this._escHtml(name)}</span>
            <span class="font-medium">$${safeAmt.toFixed(2)}</span>
          </div>`
        })

      const shippingVisible = this.hasShippingSectionTarget &&
                              !this.shippingSectionTarget.classList.contains("hidden")
      const shippingAmt = shippingVisible && this.hasShippingInputTarget
        ? (parseFloat(this.shippingInputTarget.value) || 0)
        : 0
      if (shippingAmt > 0) {
        totalAmount += shippingAmt
        rows.push(`<div class="flex justify-between text-xs text-gray-700">
          <span class="truncate flex-1 pr-2 italic">Shipping</span>
          <span class="font-medium">$${shippingAmt.toFixed(2)}</span>
        </div>`)
      }

      const label = actionType === "Refund" ? "Total Refund" : "Total Order Credits"
      const themeColor = actionType === "Refund" ? "blue" : "purple"
      content.innerHTML = rows.join("") +
        `<div class="flex justify-between text-sm font-semibold border-t border-${themeColor}-300 pt-2 mt-1 text-${themeColor}-800">
          <span>${this._escHtml(label)}</span>
          <span>$${totalAmount.toFixed(2)}</span>
        </div>`
    }
  }

  _renderSubmitState() {
    if (!this.hasSubmitBtnTarget) return
    this.submitBtnTarget.disabled = !this._isValid()
    this.submitBtnTarget.classList.toggle("opacity-50", !this._isValid())
    this.submitBtnTarget.classList.toggle("cursor-not-allowed", !this._isValid())
  }

  _isValid() {
    const actionType = this.actionTypeValue
    if (!actionType) return false
    if (actionType === "Chargeback") return true

    if (actionType === "GeneralCredits") {
      const form = this.generalCreditsFormTarget
      if (!form) return false
      const disposition = form.querySelector("select")?.value
      const amtInput = form.querySelector("input[type=number]")
      const amount = parseFloat(amtInput?.value)
      const cap = parseFloat(amtInput?.max)
      return !!disposition && !isNaN(amount) && amount > 0 && (isNaN(cap) || amount <= cap)
    }

    const selectedIds = new Set(this._selectedItemIds)
    if (selectedIds.size === 0) return false

    const itemsValid = this.itemCardTargets
      .filter(card => selectedIds.has(card.dataset.itemId))
      .every(card => {
        const activeSelect = card.querySelector("select:not([disabled])")
        const disposition = activeSelect?.value
        if (!disposition) return false

        if (NEEDS_AMOUNT.includes(actionType)) {
          const amtInput = card.querySelector("input[type=number]")
          const amt = parseFloat(amtInput?.value)
          const cap = parseFloat(amtInput?.max)
          if (isNaN(amt) || amt < 0) return false
          if (!isNaN(cap) && amt > cap) return false
        }

        return true
      })

    if (!itemsValid) return false

    // Shipping input: value must be >= 0 and within the eligible cap.
    const shippingVisible = this.hasShippingSectionTarget &&
                            !this.shippingSectionTarget.classList.contains("hidden")
    if (shippingVisible && this.hasShippingInputTarget) {
      const shippingAmt = parseFloat(this.shippingInputTarget.value)
      const shippingCap = parseFloat(this.shippingInputTarget.max)
      if (isNaN(shippingAmt) || shippingAmt < 0) return false
      if (!isNaN(shippingCap) && shippingAmt > shippingCap) return false
    }

    return true
  }

  _escHtml(str) {
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
  }

  _formatCurrency(amount) {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount)
  }
}
